// src/components/BarreOngletsBas.jsx — LA BARRE D'ONGLETS DU BAS (spécification
// exacte Dimitri 2026-08-22, validée au pixel). Téléphone seulement.
//
//   · fond BLEU CLAIR #dbeaf7, filet supérieur #c3d9ed, padding 5px 5px 6px,
//     4 px entre les onglets ;
//   · onglet : 54 px min, rayon 11, icône 19 au-dessus du libellé 8.5, 3 px ;
//   · ACTIF : FOND BLANC, ombre 0 1px 4px rgba(35,50,64,.12), icône #3d5166,
//     libellé #233240 gras ;
//   · inactif : transparent, icône ET libellé #3d5166 ; survol rgba(255,255,255,.45) ;
//   · pastille de compte : #7A2E6E (maquette v677, aubergine, jamais de rouge), blanc 9 px, rayon 20, 16 px min, en haut à
//     droite, liseré 1.5 px COULEUR DU FOND (#dbeaf7) pour la détacher.
// Orchestrateur (maquette v677) : Les ménages · Contrôle · Incidents · Demandes
// d'horaires · Stock · Avis.

import { Fragment } from 'react'
import useEtroit from '../lib/useEtroit'
import { DS } from '../lib/ui'
import { CalendarDays, Clock3, Eye, Package, Star, TriangleAlert, Home, Banknote, Sparkles,
  LayoutDashboard, MessageSquare, Briefcase, Building2, ListChecks, Settings } from 'lucide-react'

const ICONES = {
  // orchestrateur
  planning: CalendarDays, 'menage-controle': Eye, incidents: TriangleAlert, 'a-decider': Clock3,
  stock: Package, 'menage-avis': Star,
  // agent (refonte 3 onglets, 2026-08-22)
  'menage-today': CalendarDays, 'mon-travail': Star,
  // proprio (refonte 4 onglets, 2026-08-22)
  accueil: Home, revenus: Banknote, calendrier: CalendarDays, bien: Sparkles,
  // ADMIN (lot 1 accueil, 2026-08-26) — AJOUT PUREMENT ADDITIF : ces clés
  // n'existaient pas, aucun autre espace ne les demande, donc rien ne change
  // pour l'orchestrateur, l'agent ni le propriétaire.
  // ⚠️ POURQUOI C'ÉTAIT NÉCESSAIRE : sans elles, sept des neuf familles admin
  // retombaient sur l'étoile de repli — SEPT ONGLETS AU DESSIN IDENTIQUE.
  // La barre tenait géométriquement (mesuré : aucun libellé ne déborde) et
  // ne servait à rien : on ne distingue pas une navigation à ses libellés de
  // 8,5 px, on la distingue à ses dessins.
  pilotage: LayoutDashboard, communication: MessageSquare, operations: Briefcase,
  logements: Building2, 'mes-taches': ListChecks, params: Settings,
}

// ADMIN, MAQUETTE NEST v677 (`nest`) : barre gris clair, les familles
// `dansTiroir` vivent dans le menu ☰, `centre` (l'opérateur) s'assoit au milieu.
export default function BarreOngletsBas({ categories, activeId, onChoisir, badges = {}, nest = false, centre = null }) {
  const etroit = useEtroit()
  if (!etroit) return null
  const visibles = categories.filter((c) => !c.dansTiroir)
  const milieu = centre ? Math.floor(visibles.length / 2) : -1
  return (
    <>
      <style>{`
        @media (max-width: 860px){ body{padding-bottom: calc(66px + env(safe-area-inset-bottom))} }
        [data-testid^="onglet-bas-"]:not([aria-current]):hover{background:rgba(255,255,255,.45) !important}
        /* UNE FEUILLE EST OUVERTE (correction Dimitri 22/08) : la barre
           s'efface — elle peignait PAR-DESSUS le bas des feuilles (z80 devant
           z999 au compositeur, mesuré : « Incidents / Détails » invisibles
           sous elle). Le signal est posé par useVoileEcran. */
        body.fbz-voile-ouvert [data-testid="barre-onglets-bas"]{display:none !important}
        [data-variante="nest"] [data-testid^="onglet-bas-badge-"]{background:#7A2E6E !important;border-color:#F0F3F1 !important}
      `}</style>
      <nav data-testid="barre-onglets-bas" aria-label="Navigation principale"
        data-variante={nest ? 'nest' : undefined}
        style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 80,
          background: nest ? '#F0F3F1' : '#dbeaf7', borderTop: nest ? '1px solid #DCE3DF' : '1px solid #c3d9ed',
          display: 'flex', alignItems: 'stretch', gap: 4,
          padding: '5px 5px calc(6px + env(safe-area-inset-bottom))' }}>
        {visibles.map((c, i) => {
          const actif = c.id === activeId
          const badge = badges[c.id] || 0
          const label = String(c.label).replace(/^[^A-Za-zÀ-ÿ]+\s*/, '')
          const Icone = c.Icone || ICONES[c.id] || Star
          const tonIcone = nest ? (actif ? '#17211E' : '#5E6A66') : '#3d5166'
          const tonLibelle = nest ? (actif ? '#17211E' : '#5E6A66') : (actif ? '#233240' : '#3d5166')
          // LOT 2 (2026-08-26) — l'ADMIN porte les PICTOGRAMMES du proto
          // (✦ ◷ € ⌂ ✉), passés par la catégorie (`c.ic`). Les autres espaces
          // n'en passent pas : leurs dessins lucide ne bougent pas.
          return (
            <Fragment key={c.id}>
            {i === milieu ? centre : null}
            <button type="button" data-testid={`onglet-bas-${c.id}`}
              aria-current={actif ? 'page' : undefined}
              onClick={() => onChoisir(c.id)}
              style={{ flex: 1, minHeight: 54, border: 'none', borderRadius: 11,
                background: actif ? '#fff' : 'transparent',
                boxShadow: actif ? '0 1px 4px rgba(35,50,64,.12)' : 'none',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', gap: 3, padding: '4px 2px',
                position: 'relative', cursor: 'pointer', fontFamily: DS.fontBody }}>
              {c.ic
                ? <span aria-hidden="true" style={{ fontSize: 18, lineHeight: '19px',
                    color: tonIcone }}>{c.ic}</span>
                : <Icone size={19} strokeWidth={2} color={tonIcone} />}
              <span style={{ fontSize: 8.5, fontWeight: actif ? 700 : 600,
                color: tonLibelle, whiteSpace: 'nowrap' }}>{label}</span>
              {badge > 0 && (
                <span data-testid={`onglet-bas-badge-${c.id}`}
                  style={{ position: 'absolute', top: 3, right: 'calc(50% - 22px)',
                    minWidth: 16, height: 16, padding: '0 4px', borderRadius: 20,
                    background: '#7A2E6E', color: '#fff', border: '1.5px solid #dbeaf7',
                    fontSize: 9, fontWeight: 700, display: 'inline-flex',
                    alignItems: 'center', justifyContent: 'center', boxSizing: 'content-box' }}>
                  {badge}
                </span>
              )}
            </button>
            </Fragment>
          )
        })}
      </nav>
    </>
  )
}
